import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, User, Pencil } from "lucide-react";

const empty = { name: "", role: "", email: "", fav_color: "", motivation: "", bio: "", section: "equipo-general" };

const SECTIONS = [
  { value: "directoras", label: "Consejo Ejecutivo (Directoras)" },
  { value: "consejo-juvenil", label: "Consejo Juvenil" },
  { value: "equipo-general", label: "Equipo General" },
];

const AdminTeam = () => {
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState("directoras");

  const load = () => api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resetForm = () => { setEditingId(null); setForm({ ...empty, section: filter }); setFile(null); };

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({ name: m.name || "", role: m.role || "", email: m.email || "", fav_color: m.fav_color || "", motivation: m.motivation || "", bio: (m.bio || []).join("\n"), section: m.section || "equipo-general" });
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/team/${editingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Perfil actualizado");
      } else {
        await api.post("/team", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Perfil publicado");
      }
      resetForm();
      load();
    } catch { toast.error("No se pudo guardar el perfil"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este perfil?")) return;
    await api.delete(`/team/${id}`);
    toast.success("Perfil eliminado");
    load();
  };

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
      <form onSubmit={submit} data-testid="team-form" className="bg-white border border-[#E5E5E5] rounded-sm p-6 h-fit">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2">
            <Upload size={18} className="text-brand-magenta" /> {editingId ? "Editar perfil" : "Nuevo perfil (Equipo General)"}
          </h2>
          {editingId && <button type="button" onClick={resetForm} data-testid="team-cancel" className="text-sm text-brand-ink/60 hover:text-brand-ink">Cancelar</button>}
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-sm font-semibold text-brand-ink">Sección</label>
            <select data-testid="team-section" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta bg-white">
              {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Nombre</label>
            <input required data-testid="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Puesto</label>
            <input data-testid="team-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Contacto (correo)</label>
            <input data-testid="team-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Color favorito (opcional)</label>
            <input data-testid="team-favcolor" value={form.fav_color} onChange={(e) => setForm({ ...form, fav_color: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Motivación (opcional, frase corta)</label>
            <input data-testid="team-motivation" value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Semblanza (un párrafo por línea)</label>
            <textarea rows={5} data-testid="team-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Foto{editingId ? " — opcional (déjala vacía para conservar la actual)" : ""}</label>
            <input type="file" accept="image/*" data-testid="team-file" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5 w-full text-sm border border-[#E5E5E5] rounded-sm px-4 py-2.5 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-brand-ink file:text-white" />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="save-team-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-magenta text-white font-semibold py-3 rounded-sm hover:bg-brand-orange transition-colors disabled:opacity-60">
          {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Publicar perfil"}
        </button>
      </form>

      <div>
        <h2 className="font-heading text-lg font-bold text-brand-ink mb-3">Perfiles publicados</h2>
        <div className="flex gap-2 flex-wrap mb-4">
          {SECTIONS.map((s) => (
            <button key={s.value} data-testid={`team-filter-${s.value}`} onClick={() => setFilter(s.value)} className={`px-3 py-1.5 rounded-sm text-xs font-semibold border ${filter === s.value ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-[#E5E5E5] text-brand-ink"}`}>
              {s.label} ({team.filter((m) => m.section === s.value).length})
            </button>
          ))}
        </div>
        {team.filter((m) => m.section === filter).length === 0 ? (
          <p className="text-brand-ink/50">Aún no hay perfiles en esta sección.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {team.filter((m) => m.section === filter).map((m) => (
              <div key={m.id} data-testid={`admin-team-${m.id}`} className="bg-white border border-[#E5E5E5] rounded-sm p-4 flex items-center gap-3">
                {m.photo_ref ? (
                  <img src={m.photo_ref.startsWith("/") ? m.photo_ref : `${API}/media/${m.photo_ref}`} alt={m.name} className="w-12 h-12 rounded-sm object-cover object-top flex-shrink-0" />
                ) : (
                  <span className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: m.color }}><User size={20} className="text-white" /></span>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-brand-ink truncate">{m.name}</h3>
                  {m.role && <p className="text-xs text-brand-ink/50 truncate">{m.role}</p>}
                </div>
                <button data-testid={`edit-team-${m.id}`} onClick={() => startEdit(m)} className="p-2 text-brand-ink hover:text-brand-magenta"><Pencil size={16} /></button>
                <button data-testid={`delete-team-${m.id}`} onClick={() => remove(m.id)} className="p-2 text-brand-ink hover:text-red-600"><Trash2 size={17} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTeam;
